import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellOff, Loader2, ExternalLink, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import SubPageShell from "@/apps/rider/components/SubPageShell";
import { requestPushPermissionAndToken } from "@/shared/lib/firebase";

const PREF_DEFS = [
  { key: "ride_reminders",        label: "Ride reminders",        hint: "5-minute pickup pre-pings before each ride." },
  { key: "driver_arrival",        label: "Driver arrival alerts", hint: "“Your driver is arriving now.”" },
  { key: "payment_confirmations", label: "Payment confirmations", hint: "Receipts + renewal reminders." },
  { key: "service_updates",       label: "Service updates",       hint: "Rare product news and service-area changes." },
];

export default function NotificationPrefs() {
  const { user, loading: authLoading } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    return Notification.permission;
  });
  const [enabling, setEnabling] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/me/notification-prefs");
        setPrefs(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load preferences");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const toggle = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSavingKey(key);
    try {
      const { data } = await api.patch("/me/notification-prefs", { [key]: next[key] });
      setPrefs(data);
    } catch (e) {
      // Revert on failure.
      setPrefs(prefs);
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save preference");
    } finally {
      setSavingKey(null);
    }
  };

  const enablePush = async () => {
    if (permission === "denied") {
      toast.error("Notifications are blocked. Re-enable them in your device settings.");
      return;
    }
    setEnabling(true);
    try {
      let perm = Notification.permission;
      if (perm !== "granted") perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error(perm === "denied"
          ? "Notifications denied. Re-enable them in your browser/OS settings."
          : "Permission was not granted.");
        return;
      }
      const token = await requestPushPermissionAndToken({ force: true });
      if (!token) {
        toast.error("Couldn't fetch a push token. Try again from a regular Chrome/Edge tab.");
        return;
      }
      await api.post("/notifications/register-token", { token, platform: detectPlatform() });
      toast.success("Notifications enabled.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't enable notifications.");
    } finally {
      setEnabling(false);
    }
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <SubPageShell eyebrow="Preferences" title="Notifications" subtitle="Sign in to manage notification preferences."
        back="/" backLabel="Home" testId="notifications-page">
        <Link to="/login" className="inline-flex items-center gap-2 bg-[#0A1128] text-[#F9F7F4] px-5 py-3 rounded-xl text-[12px] tracking-[0.18em] uppercase font-semibold">Sign in</Link>
      </SubPageShell>
    );
  }

  return (
    <SubPageShell
      eyebrow="Preferences"
      title="Notifications"
      subtitle="Choose which Link alerts land on this device."
      back="/account"
      backLabel="Account"
      testId="notifications-page"
    >
      {/* Device permission card */}
      <div className="bg-white border hairline rounded-2xl p-4 mb-4" data-testid="notifications-permission">
        <div className="flex items-start gap-3">
          <span className={`w-10 h-10 inline-flex items-center justify-center rounded-xl shrink-0 ${
            permission === "granted" ? "bg-[#FCEADE] text-[#ed6500]" : "bg-[#F9F7F4] text-[#515E7B]"
          }`}>
            {permission === "granted" ? <Bell size={16} /> : <BellOff size={16} />}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#0A1128] text-[15px]">
              {permission === "granted" ? "Push notifications enabled" :
               permission === "denied" ? "Push notifications blocked" :
               permission === "unsupported" ? "This device doesn't support push" :
               "Push notifications off"}
            </div>
            <div className="text-xs text-[#515E7B] mt-0.5">
              {permission === "granted" ? "This device will receive alerts based on your toggles below." :
               permission === "denied" ? "Re-enable notifications in your browser or OS settings, then refresh." :
               permission === "unsupported" ? "Try a Chrome or Edge browser, or install Link to your home screen." :
               "Tap below to allow notifications."}
            </div>
            {permission !== "unsupported" && permission !== "granted" && (
              <button
                type="button"
                onClick={enablePush}
                disabled={enabling}
                className="mt-3 bg-[#0A1128] text-[#F9F7F4] px-4 py-2 rounded-lg text-[11px] tracking-[0.2em] uppercase font-semibold disabled:opacity-60 inline-flex items-center gap-2"
                data-testid="notifications-enable"
              >
                {enabling ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                {permission === "denied" ? "How to re-enable" : "Allow notifications"}
              </button>
            )}
            {permission === "denied" && (
              <a
                href="https://support.google.com/chrome/answer/3220216"
                target="_blank" rel="noopener noreferrer"
                className="text-[11px] text-[#ed6500] hover:underline mt-2 inline-flex items-center gap-1"
                data-testid="notifications-help-link"
              >
                Open browser help <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Per-category toggles */}
      <div className="bg-white border hairline rounded-2xl divide-y divide-[#E5E1D8]" data-testid="notifications-prefs-list">
        {loading ? (
          <div className="px-4 py-6 flex items-center gap-2 text-[12px] text-[#515E7B]">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          PREF_DEFS.map((p) => {
            const checked = !!(prefs && prefs[p.key]);
            const saving = savingKey === p.key;
            return (
              <div key={p.key} className="px-4 py-3.5 flex items-center gap-3" data-testid={`notif-pref-${p.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0A1128] text-[14px]">{p.label}</div>
                  <div className="text-[11.5px] text-[#515E7B] mt-0.5">{p.hint}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={checked}
                  onClick={() => toggle(p.key)}
                  disabled={saving}
                  data-testid={`notif-pref-${p.key}-toggle`}
                  data-checked={checked ? "1" : "0"}
                  className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                    checked ? "bg-[#ed6500]" : "bg-[#0A1128]/15"
                  } ${saving ? "opacity-60" : ""}`}
                >
                  <span
                    className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform"
                    style={{ transform: checked ? "translateX(20px)" : "translateX(0)" }}
                    aria-hidden="true"
                  />
                </button>
              </div>
            );
          })
        )}
      </div>

      {permission !== "granted" && permission !== "unsupported" && (
        <div className="mt-4 px-3 py-3 bg-[#FCEADE] border-l-2 border-[#ed6500] rounded-xl flex items-start gap-2.5 text-[#0A1128]" data-testid="notifications-permission-warning">
          <AlertCircle size={14} className="text-[#ed6500] shrink-0 mt-0.5" />
          <span className="text-[12px] leading-snug">
            Toggles save to your account, but this device won't actually buzz until push permission is granted above.
          </span>
        </div>
      )}
    </SubPageShell>
  );
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "web";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "web";
}
